import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

// In-memory cache for profile picture URLs (reduces API calls)
const profilePictureCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useProfileImageUrl(user, displayName, email) {
  const [profileUrl, setProfileUrl] = useState('');
  const isMountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    
    const fetchProfilePicture = async (retryCount = 0) => {
      if (!user?.id || !isMountedRef.current) return;
      
      // Check cache first
      const cacheKey = user.id;
      const cached = profilePictureCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setProfileUrl(cached.url);
        return;
      }

      const possibleExts = ['png', 'jpg', 'jpeg', 'webp', 'gif'];
      const MAX_RETRIES = 2;
      const RETRY_DELAY = 2000; // 2 seconds
      
      try {
      for (const ext of possibleExts) {
        const fileName = `${user.id}.${ext}`;
          
          try {
        // Use list to check if file exists
            const { data, error } = await supabase.storage
              .from('profile-pictures')
              .list('', { search: fileName });
            
            // Handle storage errors gracefully
            if (error) {
              // Check if it's a server error (500, 544, etc.)
              const isServerError = error.statusCode >= 500 || error.message?.includes('500') || error.message?.includes('544');
              
              if (isServerError && retryCount < MAX_RETRIES) {
                console.warn(`[Profile Picture] Storage error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${RETRY_DELAY}ms...`, error.message);
                
                // Retry after delay
                retryTimeoutRef.current = setTimeout(() => {
                  if (isMountedRef.current) {
                    fetchProfilePicture(retryCount + 1);
                  }
                }, RETRY_DELAY * Math.pow(2, retryCount)); // Exponential backoff
                
                return; // Exit and wait for retry
              }
              
              // If not retryable or max retries reached, fall through to default avatar
              console.warn('[Profile Picture] Storage error, using fallback avatar:', error.message);
              break;
            }
            
        if (data && data.some(file => file.name === fileName)) {
          const { data: urlData } = supabase.storage.from('profile-pictures').getPublicUrl(fileName);
              const publicUrl = urlData.publicUrl;
              
              // Cache the result
              profilePictureCache.set(cacheKey, {
                url: publicUrl,
                timestamp: Date.now()
              });
              
              if (isMountedRef.current) {
                setProfileUrl(publicUrl);
              }
          return;
        }
          } catch (err) {
            console.warn(`[Profile Picture] Error checking ${fileName}:`, err.message);
            // Continue to next extension
            continue;
          }
        }
        
        // No custom profile picture found - cache empty result
        profilePictureCache.set(cacheKey, {
          url: '',
          timestamp: Date.now()
        });
        
        if (isMountedRef.current) {
          setProfileUrl('');
        }
      } catch (error) {
        console.error('[Profile Picture] Unexpected error:', error);
        // Fall back to default avatar
        if (isMountedRef.current) {
          setProfileUrl('');
        }
      }
    };
    
    fetchProfilePicture();
    
    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [user]);

  // Always use Supabase bucket image if it exists
  const getProfileImageUrl = () => {
    if (profileUrl) {
      return profileUrl;
    }
    if (user?.user_metadata?.avatar_url) {
      return user.user_metadata.avatar_url;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName || email || 'User')}`;
  };

  return getProfileImageUrl();
} 